import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { jwtDecode } from 'jwt-decode';
import {
  Subject,
  fromEvent,
  merge,
  timer,
  takeUntil,
  debounceTime,
  switchMap,
  filter,
  from,
} from 'rxjs';
import { SsoState } from '../../../store/sso/sso.state';
import {
  SsoRefreshTokenAction,
  SsoSetUserInfoAction,
  SsoClearStateAction,
} from '../../../store/sso/sso.actions';
import { IDecodedToken } from '../../interfaces/sso.interface';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshManagerService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private refreshTimer$ = new Subject<void>();
  private isUserActive = true;
  private lastActivityTime = Date.now();
  private storageListener: ((event: StorageEvent) => void) | null = null;

  // Configuración
  private readonly REFRESH_BUFFER_MINUTES = 2; // Refrescar 2 minutos antes de expirar
  private readonly ACTIVITY_TIMEOUT_MINUTES = 5; // Considerar inactivo después de 5 min
  private readonly CHECK_INTERVAL_SECONDS = 30; // Verificar cada 30 segundos

  constructor(
    private store: Store,
    private ngZone: NgZone
  ) {
    this.initializeActivityTracking();
    this.initializeTokenMonitoring();
    this.initializeCrossTabSync(); // ✅ Inicializar sincronización entre pestañas
  }

  /**
   * Inicializa el tracking de actividad del usuario
   */
  private initializeActivityTracking(): void {
    // Eventos que indican actividad del usuario
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Page Visibility API para detectar cuando la pestaña está activa
    const visibilityChange$ = fromEvent(document, 'visibilitychange');

    // Merge de todos los eventos de actividad
    const userActivity$ = merge(
      ...activityEvents.map(event => fromEvent(document, event)),
      visibilityChange$
    );

    // Debounce para evitar demasiadas actualizaciones
    userActivity$
      .pipe(
        debounceTime(1000), // Agrupa eventos en ventanas de 1 segundo
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateUserActivity();
      });
  }

  /**
   * Inicializa el monitoreo del token
   */
  private initializeTokenMonitoring(): void {
    // Timer que verifica el estado del token cada X segundos
    timer(0, this.CHECK_INTERVAL_SECONDS * 1000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => from(this.checkTokenExpiration())),
        filter(shouldRefresh => shouldRefresh)
      )
      .subscribe(() => {
        this.performProactiveRefresh();
      });
  }

  /**
   * Actualiza el estado de actividad del usuario
   */
  private updateUserActivity(): void {
    this.lastActivityTime = Date.now();
    this.isUserActive = !document.hidden; // Page Visibility API
  }

  /**
   * Verifica si el token necesita ser refrescado
   */
  private checkTokenExpiration(): Promise<boolean> {
    return new Promise(resolve => {
      const token = this.store.selectSnapshot(SsoState.token);

      if (!token) {
        resolve(false);
        return;
      }

      try {
        const decoded = jwtDecode(token) as IDecodedToken;
        const now = Math.floor(Date.now() / 1000); // Tiempo actual en segundos
        const expirationTime = decoded.exp;
        const timeUntilExpiration = expirationTime - now;
        const refreshThreshold = this.REFRESH_BUFFER_MINUTES * 60; // Convertir a segundos

        // Verificar si el usuario ha estado inactivo por mucho tiempo
        const timeSinceLastActivity =
          (Date.now() - this.lastActivityTime) / 1000 / 60; // en minutos
        const isUserRecentlyActive =
          timeSinceLastActivity < this.ACTIVITY_TIMEOUT_MINUTES;

        const shouldRefresh =
          timeUntilExpiration <= refreshThreshold &&
          this.isUserActive &&
          isUserRecentlyActive;

        resolve(shouldRefresh);
      } catch (error) {
        console.error('❌ Error decodificando token:', error);
        resolve(false);
      }
    });
  }

  /**
   * Ejecuta el refresh proactivo del token
   */
  private performProactiveRefresh(): void {
    const refreshToken = this.store.selectSnapshot(SsoState.refreshToken);

    if (!refreshToken) {
      console.warn('⚠️ No hay refresh token disponible para refresh proactivo');
      return;
    }

    this.store.dispatch(new SsoRefreshTokenAction(refreshToken));
  }
  /**
   * Obtiene información del estado actual del token
   */
  getTokenInfo(): {
    timeUntilExpiration: number;
    shouldRefresh: boolean;
  } | null {
    const token = this.store.selectSnapshot(SsoState.token);

    if (!token) return null;

    try {
      const decoded = jwtDecode(token) as IDecodedToken;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiration = decoded.exp - now;
      const refreshThreshold = this.REFRESH_BUFFER_MINUTES * 60;

      return {
        timeUntilExpiration,
        shouldRefresh: timeUntilExpiration <= refreshThreshold,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Fuerza un refresh inmediato (para testing o casos especiales)
   */
  forceRefresh(): void {
    this.performProactiveRefresh();
  }

  /**
   * ✅ Inicializa la sincronización entre pestañas del navegador
   * Detecta cambios en localStorage desde otras pestañas y sincroniza el token local
   */
  private initializeCrossTabSync(): void {
    this.storageListener = (event: StorageEvent) => {
      // Solo procesar cambios en la key 'sso' de localStorage
      // (NGXS guarda cada state por separado, no bajo @@STATE)
      if (event.key === 'sso' || event.key === null) {
        this.handleStorageChange(event);
      }
    };

    window.addEventListener('storage', this.storageListener);
  }

  /**
   * ✅ Maneja los cambios en localStorage desde otras pestañas
   */
  private handleStorageChange(event: StorageEvent): void {
    try {
      // Si newValue es null, significa que se limpió el storage (logout en otra pestaña)
      if (event.newValue === null && event.oldValue !== null) {
        this.store.dispatch(new SsoClearStateAction());
        return;
      }

      // Si hay un nuevo valor, parsearlo y verificar cambios en SSO
      // event.newValue es el objeto SSO directamente (no un wrapper)
      if (event.newValue) {
        const newSsoState = JSON.parse(event.newValue);
        const oldSsoState = event.oldValue ? JSON.parse(event.oldValue) : null;

        // Verificar si el SSO tiene token
        if (newSsoState && newSsoState.token) {
          const newToken = newSsoState.token;
          const oldToken = oldSsoState?.token;

          // Obtener el token actual del store local para comparación
          const currentLocalToken = this.store.selectSnapshot(SsoState.token);

          // Si el token cambió Y es diferente al token local actual
          if (
            newToken &&
            newToken !== oldToken &&
            newToken !== currentLocalToken
          ) {
            // Actualizar el store local con los nuevos datos
            // skipVerification: true para evitar llamar verify-token
            // (el token ya fue validado en la pestaña que hizo el refresh)
            if (newSsoState.token && newSsoState.refreshToken) {
              this.store.dispatch(
                new SsoSetUserInfoAction(
                  {
                    accessToken: newSsoState.token,
                    refreshToken: newSsoState.refreshToken,
                  },
                  true // skipVerification: true
                )
              );
            }
          }

          // Si el token se eliminó en la otra pestaña (logout)
          if (!newToken && oldToken) {
            this.store.dispatch(new SsoClearStateAction());
          }
        } else if (oldSsoState?.token && !newSsoState?.token) {
          // Si tenía token pero ya no, es un logout
          this.store.dispatch(new SsoClearStateAction());
        }
      }
    } catch (error) {
      console.error(
        '❌ [TokenRefreshManager] Error al sincronizar entre pestañas:',
        error
      );
    }
  }

  ngOnDestroy(): void {
    // Limpiar listener de storage
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.refreshTimer$.complete();
  }
}
